import type { DeploymentLabel } from '../core/deploymentLabels'
import { hostDisplayName } from '../core/hostName'
import type { Coin } from '../core/types'
import { toLeaseList } from './adapters'
import { depositFigures, escrowDseq, LOW_RUNWAY_BLOCKS, runwayMinutes, sumCoins, type DepositFigures } from './deposits'
import { HOST_NOT_LISTED, labelFor } from './instances'
import type { CatalogHostDto, DomainLease, DomainLeaseList, EscrowAccountDto, EscrowDto, LeaseListDto } from './types'

// The coin primitives live with the deposit math; existing callers import them from here.
export { integerRate, normalizedState, sumCoins, type BillingAccountState } from './deposits'

export type BillingStatus = 'healthy' | 'low' | 'empty' | 'unknown'

export type BillingDepositStatus = 'running' | 'low' | 'out-of-funds' | 'waiting' | 'closed' | 'unknown'

export interface BillingDepositRow {
  dseq: string
  /** The recorded name, else "Deployment #<dseq>" (the Instances list's fallback). */
  name: string
  nameIsFallback: boolean
  /** The host's display name, "Host not listed", or null when no lease exists. */
  host: string | null
  status: BillingDepositStatus
  figures: DepositFigures
}

export interface BillingViewModel {
  deposits: {
    /** Every deposit that is not closed, most urgent first. */
    rows: BillingDepositRow[]
    /** The sum of `rows`' deposits left. Null when any is unreadable, or there are none. */
    left: Coin | null
    /** True when any part of `left` is an estimate. */
    estimated: boolean
    /** Rows a host is running: `running` and `low`. */
    activeCount: number
    waitingCount: number
    /** Closed deposits: counted, not listed. */
    closedCount: number
  }
  spending: {
    /** Per block, the running rows' rates summed. Null when any is unreadable, or nothing runs. */
    rate: Coin | null
    /** The running row with the fewest minutes left. Null when any running row's runway is unknown. */
    shortest: { dseq: string; name: string; minutes: number } | null
  }
  status: BillingStatus
  /** Running rows whose runway is at or under the shared low threshold. */
  lowRows: BillingDepositRow[]
}

export interface BillingModelInput {
  escrow: EscrowDto | null
  leases: DomainLeaseList | LeaseListDto | null
  catalogHosts: readonly CatalogHostDto[] | null
  labels: Record<string, DeploymentLabel>
  height: number | null
}

const STATUS_ORDER: Record<BillingDepositStatus, number> = { 'out-of-funds': 0, low: 1, running: 2, waiting: 3, unknown: 4, closed: 5 }

function domainLeases(leases: BillingModelInput['leases']): DomainLease[] | null {
  if (!leases || !Array.isArray(leases.leases)) return null
  const first = leases.leases[0] as unknown
  // A wire list nests each lease under `lease`; a domain list is already flat.
  return first && typeof first === 'object' && 'lease' in first ? toLeaseList(leases as LeaseListDto).leases : (leases.leases as DomainLease[])
}

function isZero(coin: Coin | null): boolean {
  return coin !== null && /^0+$/.test(coin.amount)
}

/**
 * One deposit's status, from its escrow state and its leases. The escrow says
 * whether money is held; the leases say what, if anything, is spending it.
 */
function statusOf(figures: DepositFigures, group: readonly DomainLease[] | null): BillingDepositStatus {
  if (figures.state === 'insufficient-funds') return 'out-of-funds'
  if (figures.state === 'closed') return 'closed'
  // Without the leases, an open deposit could be running or waiting: unknown.
  if (figures.state === 'unknown' || group === null) return 'unknown'
  if (group.some((lease) => lease.state === 'insufficient-funds')) return 'out-of-funds'
  if (group.some((lease) => lease.state === 'active')) {
    return figures.runwayBlocks !== null && figures.runwayBlocks <= LOW_RUNWAY_BLOCKS ? 'low' : 'running'
  }
  // `MsgCreateDeployment` opens the deposit before any lease exists. A closed
  // lease on a still-open deployment is a provider-side close, which pauses
  // the group: no offers follow, so it is not "waiting".
  if (group.length === 0) return 'waiting'
  return 'unknown'
}

function byUrgency(a: BillingDepositRow, b: BillingDepositRow): number {
  const order = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
  if (order !== 0) return order
  const left = a.figures.runwayBlocks ?? Number.POSITIVE_INFINITY
  const right = b.figures.runwayBlocks ?? Number.POSITIVE_INFINITY
  if (left !== right) return left < right ? -1 : 1
  const newer = (/^\d+$/.test(b.dseq) ? BigInt(b.dseq) : 0n) - (/^\d+$/.test(a.dseq) ? BigInt(a.dseq) : 0n)
  return newer === 0n ? 0 : newer > 0n ? 1 : -1
}

/**
 * Billing's model: every deposit joined with its leases, host and name, plus
 * the page-level figures drawn from them. All deposit arithmetic is
 * `data/deposits.ts`'s, so Billing, Overview and Instances agree.
 */
export function buildBillingModel(input: BillingModelInput): BillingViewModel {
  const escrow = input.escrow
  const source: EscrowAccountDto[] = Array.isArray(escrow?.accounts)
    ? escrow.accounts.filter((account): account is EscrowAccountDto => Boolean(account) && typeof account === 'object' && account.scope !== 'bid')
    : []
  const leases = domainLeases(input.leases)
  const hostsByOwner = new Map((input.catalogHosts ?? []).map((host) => [host.owner, host]))

  const all = source.map((account): BillingDepositRow => {
    const dseq = escrowDseq(account)
    const group = leases ? leases.filter((lease) => lease.dseq === dseq) : null
    const figures = depositFigures(account, group ?? [], input.height)
    // The lease that speaks for the host: an active one, else any.
    const lead = group?.find((lease) => lease.state === 'active') ?? group?.[0]
    const catalogHost = lead ? hostsByOwner.get(lead.provider.address) : undefined
    const { label } = labelFor(dseq, input.labels)
    return {
      dseq,
      name: label.name,
      nameIsFallback: label.source === 'fallback',
      host: lead ? (catalogHost ? hostDisplayName(catalogHost) : HOST_NOT_LISTED) : null,
      status: statusOf(figures, group),
      figures,
    }
  })

  const rows = all.filter((row) => row.status !== 'closed').sort(byUrgency)
  const running = rows.filter((row) => row.status === 'running' || row.status === 'low')
  const lowRows = rows.filter((row) => row.status === 'low')

  // No partial sums: one unreadable part makes the total unknown.
  const left = rows.length > 0 ? sumCoins(rows.map((row) => row.figures.left)) : null
  const rate = running.length > 0 ? sumCoins(running.map((row) => row.figures.rate)) : null
  const shortestRow = running.length > 0 && running.every((row) => row.figures.runwayBlocks !== null)
    ? [...running].sort((a, b) => a.figures.runwayBlocks! - b.figures.runwayBlocks!)[0]!
    : null

  const balance = source.length > 0 ? sumCoins([escrow?.totals?.balance ?? null]) : null
  const active = source.filter((account) => all.find((row) => row.dseq === escrowDseq(account))?.figures.state === 'active')
  let status: BillingStatus
  // No escrow, or a list the console cannot read, is not "no deposits".
  if (!escrow || !Array.isArray(escrow.accounts)) status = 'unknown'
  else if (source.length === 0) status = 'empty'
  else if (balance === null) status = 'unknown'
  // "Running low" is a call to action, and it only means anything while
  // something is running that could stop. A tenant whose escrow accounts have
  // all closed has a zero balance by definition; telling them their balance
  // "needs attention" asks them to fund a workload that no longer exists.
  // An account nothing could classify is still not evidence that nothing runs,
  // so it reports unknown rather than borrowing the reassurance of "Available".
  else if (active.length === 0)
    status = isZero(balance) ? 'empty' : all.some((row) => row.figures.state === 'unknown') ? 'unknown' : 'healthy'
  else if (isZero(balance) || lowRows.length > 0) status = 'low'
  // Something open that the console cannot classify, or a runway it cannot
  // compute, is not evidence of health.
  else if (rows.some((row) => row.status === 'unknown') || (running.length > 0 && (rate === null || shortestRow === null))) status = 'unknown'
  else status = 'healthy'

  return {
    deposits: {
      rows,
      left,
      estimated: rows.some((row) => row.figures.estimated),
      activeCount: running.length,
      waitingCount: rows.filter((row) => row.status === 'waiting').length,
      closedCount: all.length - rows.length,
    },
    spending: {
      rate,
      shortest: shortestRow ? { dseq: shortestRow.dseq, name: shortestRow.name, minutes: runwayMinutes(shortestRow.figures.runwayBlocks!) } : null,
    },
    status,
    lowRows,
  }
}
